import React, { useEffect, useState } from "react";

import styles from "./GiftSheet.module.css";
import { Sheet } from "../Sheet/Sheet";
import { GIFT_MESSAGE_MAX, GIFT_NAME_MAX, giftRequest } from "../../utils/gift";

/**
 * What goes on the gift, before paying for it.
 *
 * Everything but the sender's name is optional: a gift with no message is
 * still a gift, and making someone write one before they can pay is how a
 * gift becomes an abandoned cart. The name is prefilled from the account.
 */
export const GiftSheet = ({ open, planLabel, defaultSender = "", busy = false, error = "", onClose, onContinue }) => {
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [sender, setSender] = useState("");

  // Fresh each time it opens.
  useEffect(() => {
    if (!open) return;
    setRecipient("");
    setMessage("");
    setSender("");
  }, [open]);

  // The account's first name as the sender, unless they've typed one already
  // (the profile can arrive after the sheet opens).
  useEffect(() => {
    if (!open || !defaultSender) return;
    setSender((s) => s || String(defaultSender).slice(0, GIFT_NAME_MAX));
  }, [open, defaultSender]);

  const canContinue = sender.trim().length > 0 && !busy;

  const submit = (e) => {
    e.preventDefault();
    if (!canContinue) return;
    onContinue(giftRequest({ sender, recipient, message }));
  };

  return (
    <Sheet open={open} onClose={busy ? () => {} : onClose} dismissible={!busy} label="Send as a gift">
      <form onSubmit={submit} noValidate>
        <p className={styles.eyebrow}>A gift</p>
        <p className={styles.title}>Gift {planLabel}</p>
        <p className={styles.body}>Pay now and send them a link. When they claim it, the plan is theirs.</p>

        <div className={styles.fields}>
          <div>
            <label className={styles.label} htmlFor="gift-to">To (optional)</label>
            <div className={styles.field}>
              <input
                id="gift-to"
                className={styles.input}
                value={recipient}
                maxLength={GIFT_NAME_MAX}
                placeholder="Their name"
                autoComplete="off"
                autoCapitalize="words"
                onChange={(e) => setRecipient(e.target.value.slice(0, GIFT_NAME_MAX))}
              />
            </div>
          </div>

          <div>
            <div className={styles.labelRow}>
              <label className={styles.label} htmlFor="gift-message">Message (optional)</label>
              <span className={styles.counter} aria-live="polite">{message.length}/{GIFT_MESSAGE_MAX}</span>
            </div>
            <div className={`${styles.field} ${styles.fieldArea}`}>
              <textarea
                id="gift-message"
                className={`${styles.input} ${styles.textarea}`}
                value={message}
                maxLength={GIFT_MESSAGE_MAX}
                rows={3}
                placeholder="Hope you enjoy it!"
                onChange={(e) => setMessage(e.target.value.slice(0, GIFT_MESSAGE_MAX))}
              />
            </div>
          </div>

          <div>
            <label className={styles.label} htmlFor="gift-from">From</label>
            <div className={styles.field}>
              <input
                id="gift-from"
                className={styles.input}
                value={sender}
                maxLength={GIFT_NAME_MAX}
                placeholder="Your name"
                autoComplete="given-name"
                autoCapitalize="words"
                required
                onChange={(e) => setSender(e.target.value.slice(0, GIFT_NAME_MAX))}
              />
            </div>
          </div>
        </div>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <button type="submit" className={styles.primary} disabled={!canContinue}>
          {busy ? "Please wait…" : "Continue to pay"}
        </button>
        <button type="button" className={styles.ghost} onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </form>
    </Sheet>
  );
};

export default GiftSheet;
